import type {
	TopologySelectionRuntimeSourceResolveInput,
	TopologySelectionRuntimeSourceResolutionResult,
	TopologySelectionRuntimeSourceResolverDependencies,
} from '../../../../../types/reference/sketch-entity-reference/TopologySelectionRuntimeSourceResolver';
import {
	resolveExternalGeometrySourceRefEntityId,
	resolveTopologyEndpointAnchorId,
} from '../../model/ExternalGeometrySourceDescriptor';
import {
	createExecutionDiagnostic,
} from '../../model/SketchEntityReferenceDiagnostics';

/**
 * 将当前 Convert Entities 选择会话的纯数据 topology 快照解析为可投影来源。
 *
 * 该模块封装“信任同批 Worker 查询、禁止再次和 sidecar 裁决”的全部规则，调用者
 * 只需提供已确认的 descriptor 与对应快照；历史重建在完成重定位后复用同一解析规则。
 */
export class TopologySelectionRuntimeSourceResolver {
	/**
	 * @param dependencies 当前选择解析复用的曲线拟合能力。
	 */
	public constructor(
		private readonly dependencies: TopologySelectionRuntimeSourceResolverDependencies,
	) {}

	/**
	 * 解析一条当前选择来源。
	 *
	 * @param input canonical descriptor 与同批纯数据快照。
	 * @returns 可投影来源或明确诊断。
	 */
	public resolve(
		input: TopologySelectionRuntimeSourceResolveInput,
	): TopologySelectionRuntimeSourceResolutionResult {
		if (!this.matchesDescriptor(input)) {
			return this.createFailure(
				input,
				'input-invalid',
				'当前选择快照与 canonical descriptor 不一致',
			);
		}
		if (input.runtimeSource.exactConic) {
			return this.resolveExactConic(input);
		}
		if (input.descriptor.locator.resolutionLevel === 'exact' && input.descriptor.locator.curveKind !== 'line') {
			return this.createFailure(input, 'source-missing', '缺少 BRep 精确曲线参数，不能退回采样');
		}
		if (
			input.descriptor.locator.resolutionLevel === 'sampled-only'
			|| input.descriptor.locator.resolutionLevel === 'fitted-exact'
		) {
			return this.resolveSampled(input);
		}
		return this.resolveLine(input);
	}

	/**
	 * 将 BRep 的参数方程直接适配到现有投影器，绝不再从采样反推参数。
	 * @param input 同一来源的精确世界曲线和拓扑身份。
	 * @returns 圆/圆弧或椭圆/椭圆弧，非法精确载荷显式失败。
	 */
	private resolveExactConic(input: TopologySelectionRuntimeSourceResolveInput): TopologySelectionRuntimeSourceResolutionResult {
		const conic = input.runtimeSource.exactConic!;
		const radiusX = Math.hypot(...conic.xVector);
		const radiusY = Math.hypot(...conic.yVector);
		const span = conic.lastParameter - conic.firstParameter;
		if (![...conic.center, ...conic.xVector, ...conic.yVector, span, conic.firstParameter].every(Number.isFinite)
			|| radiusX <= 0 || radiusY <= 0 || span <= 0) {
			return this.createFailure(input, 'input-invalid', 'BRep 精确曲线参数无效，不能退回采样');
		}
		// 闭合以完整周期裁剪区间确定；短弧即使端点接近也不能变成整圆。
		const closed = Math.abs(span - Math.PI * 2) <= 1e-12;
		const base = {
			sourceDescriptor: {...input.descriptor, locator: {...input.descriptor.locator, resolutionLevel: 'exact' as const, fitMetadata: undefined}},
			diagnostics: [], centerWorld: [...conic.center] as [number, number, number],
			planeXAxisWorld: conic.xVector.map(value => value / radiusX) as [number, number, number],
			planeYAxisWorld: conic.yVector.map(value => value / radiusY) as [number, number, number],
		};
		const angles = {startAngle: conic.firstParameter, endAngle: conic.lastParameter, clockwise: false};
		const orthogonality = conic.xVector.reduce((sum, value, index) => sum + value * conic.yVector[index]!, 0) / (radiusX * radiusY);
		const circular = Math.abs(radiusX - radiusY) <= Math.max(radiusX, radiusY) * 1e-12 && Math.abs(orthogonality) <= 1e-12;
		const source = circular
			? closed
				? {...base, sourceKind: 'circle' as const, radius: radiusX}
				: {...base, ...angles, sourceKind: 'arc' as const, radius: radiusX}
			: closed
				? {...base, sourceKind: 'ellipse' as const, radiusX, radiusY, rotation: 0}
				: {...base, ...angles, sourceKind: 'ellipse-arc' as const, radiusX, radiusY, rotation: 0};
		return {resolvedSource: this.attachEndpointAnchorIds(source, input), diagnostics: []};
	}

	/** 解析精确直线快照。 */
	private resolveLine(
		input: TopologySelectionRuntimeSourceResolveInput,
	): TopologySelectionRuntimeSourceResolutionResult {
		const worldLine = input.runtimeSource.worldLine;
		if (!worldLine) {
			return this.createFailure(
				input,
				'source-missing',
				'当前选择快照缺少可读取的直线几何',
			);
		}
		return {
			resolvedSource: this.attachEndpointAnchorIds(
				{
					sourceKind: 'line',
					sourceDescriptor: input.descriptor,
					diagnostics: [],
					startWorld: [...worldLine.start],
					endWorld: [...worldLine.end],
				},
				input,
			),
			diagnostics: [],
		};
	}

	/** 解析采样曲线快照。 */
	private resolveSampled(
		input: TopologySelectionRuntimeSourceResolveInput,
	): TopologySelectionRuntimeSourceResolutionResult {
		const fittingResult = this.dependencies.sampledCurveFittingService.fit({
			descriptor: input.descriptor,
			sampledPointWorlds: input.runtimeSource.sampledPointWorlds ?? [],
			endpointAnchorRefs: input.runtimeSource.endpointAnchorRefs,
		});
		if (fittingResult.status === 'failed') {
			return {
				resolvedSource: null,
				diagnostics: fittingResult.diagnostics,
			};
		}
		return {
			resolvedSource: this.attachEndpointAnchorIds(
				fittingResult.resolvedSource,
				input,
			),
			diagnostics: fittingResult.resolvedSource.diagnostics,
		};
	}

	/** 校验快照属于当前 descriptor。 */
	private matchesDescriptor(
		input: TopologySelectionRuntimeSourceResolveInput,
	): boolean {
		const {descriptor, runtimeSource} = input;
		return runtimeSource.edgeIndex === descriptor.locator.edgeIndex
			&& (
				!descriptor.locator.persistentRef
				|| runtimeSource.persistentRef === descriptor.locator.persistentRef
			)
			&& (
				!descriptor.locator.subEntityId
				|| runtimeSource.subEntityId === descriptor.locator.subEntityId
			)
			&& (
				!descriptor.owner.resultObjectId
				|| !runtimeSource.topologyMemberId
				|| runtimeSource.topologyMemberId === descriptor.owner.resultObjectId
			);
	}

	/** 把纯数据端点身份附加到解析结果。 */
	private attachEndpointAnchorIds<
		TSource extends NonNullable<
			TopologySelectionRuntimeSourceResolutionResult['resolvedSource']
		>,
	>(
		source: TSource,
		input: TopologySelectionRuntimeSourceResolveInput,
	): TSource {
		const refs = input.runtimeSource.endpointAnchorRefs;
		if (!refs?.start && !refs?.end) {
			return source;
		}
		return {
			...source,
			endpointAnchorIds: {
				start: refs.start
					? resolveTopologyEndpointAnchorId(input.descriptor.owner, refs.start)
					: undefined,
				end: refs.end
					? resolveTopologyEndpointAnchorId(input.descriptor.owner, refs.end)
					: undefined,
			},
		} as TSource;
	}

	/** 创建带来源上下文的失败结果。 */
	private createFailure(
		input: TopologySelectionRuntimeSourceResolveInput,
		kind: 'input-invalid' | 'source-missing',
		message: string,
	): TopologySelectionRuntimeSourceResolutionResult {
		const sourceRefEntityId = resolveExternalGeometrySourceRefEntityId(
			input.descriptor,
		);
		return {
			resolvedSource: null,
			diagnostics: [createExecutionDiagnostic({
				kind,
				level: kind === 'input-invalid' ? 'error' : 'warning',
				message: `Convert Entities ${message}: ${sourceRefEntityId}`,
				sourceRefEntityId,
			})],
		};
	}
}
